'use client';

import './gallery-airbnb.scss';

import { formatDuration } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { FlexibleContentProps } from '@sola/schema/lib/schema.js';
import { getHeaderDisplayLevel } from '@sola/utils/assembly.js';

import { Image } from '../../../atoms/image/image';
import { FeatureCard } from '../../../molecules/feature-card/feature-card';
import { HeaderGroup } from '../../../molecules/header-group/header-group';
import {
  Lightbox,
  LightboxTrigger,
} from '../../../molecules/lightbox/lightbox';
import { getPlaceholderItems } from '../../../utils/storybook';
import { getBaseSizeProps } from '../../../utils/text-utils';

export const GalleryAirBnb: React.FC<FlexibleContentProps> = ({
  seoLevel = 2,
  media,
  items,
  ...props
}) => {
  const { t } = useTranslation(['core']);

  const hasHeaderContent = !!props.title || !!props.body;
  const hasHeaderCard = hasHeaderContent && media?.image;
  const hasHeaderGroup = hasHeaderContent && !media?.image;
  const headerDisplayLevel = getHeaderDisplayLevel(seoLevel);

  const galleryItems =
    !items || items.length < 3 ? getPlaceholderItems(items, 3) : items;

  return (
    <section className="GalleryAirBnb component">
      <div className="GalleryAirBnb__container container flex flex-col gap-comfy-xl lg:gap-roomy-xs">
        {hasHeaderCard && (
          <FeatureCard
            {...props}
            {...getBaseSizeProps(headerDisplayLevel)}
            seoLevel={seoLevel}
            media={media}
            imagePlacement="right"
            variant="horizontal"
            inline
          />
        )}
        {hasHeaderGroup && <HeaderGroup {...props} seoLevel={seoLevel} />}

        <div className="GalleryAirBnb__grid gap-snug-xl lg:gap-comfy-sm">
          {galleryItems.slice(0, 3).map((slide, index) => (
            <div
              key={['slide', index, slide.title].join('-')}
              className="GalleryAirBnb__grid-item"
            >
              <Lightbox
                key={['lightbox', index, slide.title].join('-')}
                items={galleryItems}
                startIndex={index}
              >
                <LightboxTrigger
                  className="Lightbox__trigger w-full h-full"
                  ariaLabel={t('core:gallery.open')}
                >
                  <div className="GalleryAirBnb__image">
                    {index === 2 && (
                      <div className="GalleryAirBnb__overlay label-potent label-md md:label-lg lg:label-xl">
                        <span className="flex items-center gap-snug-lg md:gap-snug-xl lg:gap-comfy-xs">
                          {t('core:gallery.seeMore')}
                          <ArkIcon
                            iconName="arrow-right"
                            className="GalleryAirBnb__icon"
                          />
                        </span>
                      </div>
                    )}
                    {index !== 2 && slide.media?.video && (
                      <div className="Carousel__video-thumb">
                        <div className="Carousel__video-thumb__overlay" />
                        <div className="Carousel__video-thumb__info">
                          {slide.media.video.duration && (
                            <div className="label-xl label-potent p-comfy-lg">
                              {formatDuration(slide.media.video.duration)}
                            </div>
                          )}
                        </div>
                        <div className="Carousel__video-thumb__play">
                          <ArkIcon iconName="play" />
                        </div>
                      </div>
                    )}
                    <Image
                      image={slide.media?.image}
                      hasPlaceholder
                      aspectRatio={index === 0 ? '4-3' : undefined}
                    />
                  </div>
                </LightboxTrigger>
              </Lightbox>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
